import React, { useState } from 'react';
import { User, Scale, Ruler, Calendar, Target, TrendingUp, TrendingDown, Minus, Calculator, Flame, Drumstick, Wheat, Droplet } from 'lucide-react';
import api from '../api';

const ProfileSetup = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({
    age: '',
    gender: '',
    height: '',
    weight: '',
    goalType: '',
    goalSpeed: ''
  });
  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  // Calculate macro targets based on profile data
  const calculateMacroTargets = () => {
    if (!profile.age || !profile.gender || !profile.height || !profile.weight || !profile.goalType) {
      return null;
    }

    const age = parseInt(profile.age);
    const height = parseInt(profile.height);
    const weight = parseFloat(profile.weight);
    const goalSpeed = parseFloat(profile.goalSpeed) || 0;

    // Calculate BMR using Mifflin-St Jeor equation
    let bmr;
    if (profile.gender === 'male') {
      bmr = 10 * weight + 6.25 * height - 5 * age + 5;
    } else {
      bmr = 10 * weight + 6.25 * height - 5 * age - 161;
    }

    // Apply goal adjustments
    let targetCalories = bmr;
    if (profile.goalType === 'lose') {
      targetCalories = bmr - (goalSpeed * 1000); // 1000 cal deficit per kg per week
    } else if (profile.goalType === 'gain') {
      targetCalories = bmr + (goalSpeed * 1000); // 1000 cal surplus per kg per week
    }

    // Calculate macros
    const weightInPounds = weight * 2.20462;
    const protein = Math.round(weightInPounds); // 1g per pound
    const proteinCalories = protein * 4;
    
    const fatCalories = Math.round(targetCalories * 0.25); // 25% of total calories
    const fat = Math.round(fatCalories / 9);
    
    const remainingCalories = targetCalories - proteinCalories - fatCalories;
    const carbs = Math.round(remainingCalories / 4);

    return {
      calories: Math.round(targetCalories),
      protein,
      fat,
      carbs,
      bmr: Math.round(bmr)
    };
  };

  const nextStep = () => {
    setStep(prev => prev + 1);
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      // Validate all fields
      if (!profile.age || !profile.gender || !profile.height || !profile.weight || !profile.goalType || !profile.goalSpeed) {
        throw new Error('Please fill in all fields');
      }

      const response = await api.post('/auth/profile', {
        age: parseInt(profile.age),
        gender: profile.gender,
        height: parseInt(profile.height),
        weight: parseFloat(profile.weight),
        goalType: profile.goalType,
        goalSpeed: parseFloat(profile.goalSpeed)
      });      console.log('Profile setup successful:', response.data);
      
      // Update user data in localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      userData.profileCompleted = true;
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Force page reload to update authentication state
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <User className="text-emerald-600" size={32} />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Basic Information</h2>
        <p className="text-gray-600">Let's start with some basic details about you</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            <Calendar className="inline w-4 h-4 mr-1" />
            Age
          </label>
          <input
            type="number"
            value={profile.age}
            onChange={(e) => handleChange('age', e.target.value)}
            className="w-full p-4 border border-emerald-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="25"
            min="13"
            max="100"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            <User className="inline w-4 h-4 mr-1" />
            Gender
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => handleChange('gender', 'male')}
              className={`p-4 border rounded-xl font-medium transition-all ${
                profile.gender === 'male'
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
              }`}
            >
              Male
            </button>
            <button
              type="button"
              onClick={() => handleChange('gender', 'female')}
              className={`p-4 border rounded-xl font-medium transition-all ${
                profile.gender === 'female'
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
              }`}
            >
              Female
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            <Ruler className="inline w-4 h-4 mr-1" />
            Height (cm)
          </label>
          <input
            type="number"
            value={profile.height}
            onChange={(e) => handleChange('height', e.target.value)}
            className="w-full p-4 border border-emerald-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="170"
            min="100"
            max="250"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            <Scale className="inline w-4 h-4 mr-1" />
            Weight (kg)
          </label>
          <input
            type="number"
            value={profile.weight}
            onChange={(e) => handleChange('weight', e.target.value)}
            className="w-full p-4 border border-emerald-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="70"
            min="30"
            max="300"
            step="0.1"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={nextStep}
          disabled={!profile.age || !profile.gender || !profile.height || !profile.weight}
          className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 text-white px-8 py-3 rounded-xl font-semibold transition-all"
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Target className="text-emerald-600" size={32} />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Your Goals</h2>
        <p className="text-gray-600">What would you like to achieve?</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-4">Goal Type</label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            type="button"
            onClick={() => handleChange('goalType', 'lose')}
            className={`p-6 border rounded-xl text-center transition-all ${
              profile.goalType === 'lose'
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
            }`}
          >
            <TrendingDown className="mx-auto mb-2" size={24} />
            <div className="font-semibold">Lose Weight</div>
            <div className="text-sm text-gray-600">Burn fat and get lean</div>
          </button>

          <button
            type="button"
            onClick={() => handleChange('goalType', 'maintain')}
            className={`p-6 border rounded-xl text-center transition-all ${
              profile.goalType === 'maintain'
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
            }`}
          >
            <Minus className="mx-auto mb-2" size={24} />
            <div className="font-semibold">Maintain Weight</div>
            <div className="text-sm text-gray-600">Stay at current weight</div>
          </button>

          <button
            type="button"
            onClick={() => handleChange('goalType', 'gain')}
            className={`p-6 border rounded-xl text-center transition-all ${
              profile.goalType === 'gain'
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
            }`}
          >
            <TrendingUp className="mx-auto mb-2" size={24} />
            <div className="font-semibold">Gain Weight</div>
            <div className="text-sm text-gray-600">Build muscle and size</div>
          </button>
        </div>
      </div>

      {profile.goalType !== 'maintain' && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-4">
            Goal Speed ({profile.goalType === 'lose' ? 'Weight Loss' : 'Weight Gain'})
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              type="button"
              onClick={() => handleChange('goalSpeed', '0.25')}
              className={`p-4 border rounded-xl text-center transition-all ${
                profile.goalSpeed === '0.25'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <div className="font-semibold">Mild</div>
              <div className="text-sm">0.25 kg/week</div>
            </button>

            <button
              type="button"
              onClick={() => handleChange('goalSpeed', '0.5')}
              className={`p-4 border rounded-xl text-center transition-all ${
                profile.goalSpeed === '0.5'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <div className="font-semibold">Moderate</div>
              <div className="text-sm">0.5 kg/week</div>
            </button>

            <button
              type="button"
              onClick={() => handleChange('goalSpeed', '1.0')}
              className={`p-4 border rounded-xl text-center transition-all ${
                profile.goalSpeed === '1.0'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <div className="font-semibold">Fast</div>
              <div className="text-sm">1.0 kg/week</div>
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={prevStep}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-xl font-semibold transition-all"
        >
          Back
        </button>        <button
          onClick={nextStep}
          disabled={!profile.goalType || (profile.goalType !== 'maintain' && !profile.goalSpeed)}
          className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 text-white px-8 py-3 rounded-xl font-semibold transition-all"
        >
          Next: Review Targets
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => {
    const targets = calculateMacroTargets();
    
    if (!targets) {
      return (
        <div className="text-center">
          <p className="text-red-600">Unable to calculate targets. Please go back and check your information.</p>
        </div>
      );
    }

    const goalTypeText = {
      lose: 'Weight Loss',
      maintain: 'Weight Maintenance',
      gain: 'Weight Gain'
    };

    const goalSpeedText = {
      '0.25': 'Mild (0.25 kg/week)',
      '0.5': 'Moderate (0.5 kg/week)',
      '1.0': 'Fast (1.0 kg/week)'
    };

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calculator className="text-emerald-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Your Personalized Targets</h2>
          <p className="text-gray-600">Based on your profile, here are your daily macro targets</p>
        </div>

        {/* Profile Summary */}
        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Profile Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Age:</span>
              <div className="font-medium">{profile.age} years</div>
            </div>
            <div>
              <span className="text-gray-600">Gender:</span>
              <div className="font-medium capitalize">{profile.gender}</div>
            </div>
            <div>
              <span className="text-gray-600">Height:</span>
              <div className="font-medium">{profile.height} cm</div>
            </div>
            <div>
              <span className="text-gray-600">Weight:</span>
              <div className="font-medium">{profile.weight} kg</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Goal:</span>
                <div className="font-medium">{goalTypeText[profile.goalType]}</div>
              </div>
              {profile.goalType !== 'maintain' && (
                <div>
                  <span className="text-gray-600">Speed:</span>
                  <div className="font-medium">{goalSpeedText[profile.goalSpeed]}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* BMR Information */}
        <div className="bg-blue-50 rounded-xl p-6 mb-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-2">Your Base Metabolic Rate (BMR)</h3>
          <div className="text-3xl font-bold text-blue-900 mb-2">{targets.bmr} kcal/day</div>
          <p className="text-blue-700 text-sm">This is the number of calories your body burns at rest</p>
        </div>

        {/* Macro Targets */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Calories */}
          <div className="bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200 rounded-xl p-6 text-center">
            <Flame className="mx-auto mb-3 text-orange-600" size={32} />
            <div className="text-2xl font-bold text-orange-800 mb-1">{targets.calories}</div>
            <div className="text-sm font-medium text-orange-700">Calories</div>
            <div className="text-xs text-orange-600 mt-1">kcal/day</div>
          </div>

          {/* Protein */}
          <div className="bg-gradient-to-br from-red-50 to-pink-50 border border-red-200 rounded-xl p-6 text-center">
            <Drumstick className="mx-auto mb-3 text-red-600" size={32} />
            <div className="text-2xl font-bold text-red-800 mb-1">{targets.protein}g</div>
            <div className="text-sm font-medium text-red-700">Protein</div>
            <div className="text-xs text-red-600 mt-1">{targets.protein * 4} kcal</div>
          </div>

          {/* Carbs */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 text-center">
            <Wheat className="mx-auto mb-3 text-blue-600" size={32} />
            <div className="text-2xl font-bold text-blue-800 mb-1">{targets.carbs}g</div>
            <div className="text-sm font-medium text-blue-700">Carbs</div>
            <div className="text-xs text-blue-600 mt-1">{targets.carbs * 4} kcal</div>
          </div>

          {/* Fat */}
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-6 text-center">
            <Droplet className="mx-auto mb-3 text-yellow-600" size={32} />
            <div className="text-2xl font-bold text-yellow-800 mb-1">{targets.fat}g</div>
            <div className="text-sm font-medium text-yellow-700">Fat</div>
            <div className="text-xs text-yellow-600 mt-1">{targets.fat * 9} kcal</div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-amber-800 mb-3">📝 Important Notes</h3>
          <ul className="text-sm text-amber-700 space-y-2">
            <li>• These targets are based on the Mifflin-St Jeor equation and standard macro distribution</li>
            <li>• Protein is set to 1g per pound of body weight for optimal muscle maintenance</li>
            <li>• Fat accounts for 25% of total calories for hormone production and health</li>
            <li>• Remaining calories are allocated to carbohydrates for energy</li>
            {targets.calories < 1000 && (
              <li className="text-red-600 font-medium">⚠️ Your target calories are very low. Consider a slower weight loss pace for better health and sustainability.</li>
            )}
          </ul>
        </div>

        <div className="flex justify-between">
          <button
            onClick={prevStep}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-xl font-semibold transition-all"
          >
            Back
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 text-white px-8 py-3 rounded-xl font-semibold transition-all"
          >
            {loading ? 'Setting up...' : 'Complete Setup'}
          </button>
        </div>
      </div>
    );
  };  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50/30 flex items-center justify-center p-4">
      <div className="bg-white/90 backdrop-blur-sm border border-emerald-100/50 rounded-2xl p-8 w-full max-w-4xl shadow-2xl">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Step {step} of 3</span>
            <span className="text-sm font-medium text-gray-600">{Math.round((step / 3) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
            ></div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
            {error}
          </div>
        )}

        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>
    </div>
  );
};

export default ProfileSetup;
